import { isFlightItem } from 'checkout/lib/utils/flights/cart'
import { getCheckoutFlightV2SearchUrl, getStandaloneFlightsV2SearchUrl } from 'checkout/lib/utils/flights/links'
import { arrayToMap, arrayToObject, nonNullable, sum } from 'lib/array/arrayUtils'
import {
  buildDateChangeFlightItemBreakdownView,
  buildFlightItemBreakdownView,
  formatFareType,
  formatFlightPassengersShorthand, formatJourneyShorthand, formatJourneySummaryTitle,
} from 'lib/checkout/flights/format'
import { createSelector } from 'reselect'

import { getDateChangeJourneyV2IdKey, getFlightLoyaltyProductType, getFlightLoyaltyProductTypeRank, getJourneyV2IdKey, hasCheckoutFlightItemsWithEmbeddedInsurance } from 'lib/flights/flightUtils'
import { aggregateFlightServiceRows, type FlightServiceRowData } from 'checkout/Components/Shared/Summary/BreakdownItems/flightServiceRows'
import { convertJourneyV2ToJourneyFlight } from 'api/lib/convertJourneyV2'
import type { FlightsFareTypes } from 'constants/flight'
import { FlightViewTypes } from 'constants/flight'
import { findPostPurchaseCheckout, isBookingProtectionItem, isInsuranceItem, isLuxPlusSubscriptionItem } from 'lib/checkout/checkoutUtils'
import { reselectShallowEqual } from 'lib/selector/selectorUtils'
import { getAllOrderItems, isBookingProtectionOrderItem, isFlightOrderItem, isInsuranceOrderItem, isLuxPlusSubscriptionOrderItem } from 'lib/order/orderUtils'
import { getAgentHubCommissionItemKey } from 'lib/agentHub/agentHubUtils'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import type { IntlShape } from 'react-intl'

function formatTravellerCount(intl: IntlShape, type: string, count: number): string {
  switch (type) {
    case 'adult':
      return intl.formatMessage(
        { id: 'KYBPIR6p9x', defaultMessage: '{count, plural, one {# adult} other {# adults}}', description: 'Adult traveller count for flight change fee breakdown' },
        { count },
      )
    case 'child':
      return intl.formatMessage(
        { id: 'YpTzOh+I49', defaultMessage: '{count, plural, one {# child} other {# children}}', description: 'Child traveller count for flight change fee breakdown' },
        { count },
      )
    case 'infant':
      return intl.formatMessage(
        { id: '6EGVLkiJYw', defaultMessage: '{count, plural, one {# infant} other {# infants}}', description: 'Infant traveller count for flight change fee breakdown' },
        { count },
      )
    default:
      return `${count} ${type}`
  }
}

const selectFlightsSeatSelectionsByKey = (state: App.State) => state.flights.seatSelections ?? {}
const selectCheckoutSeatSelectionsByKey = (state: App.State) => state.checkout.form.flightSeatSelections ?? {}

export const getFlightSeatSelectionsByKey = createSelector(
  [selectCheckoutSeatSelectionsByKey, selectFlightsSeatSelectionsByKey],
  (checkoutSeatSelections, flightSeatSelections): Record<string, Record<string, Record<string, App.FlightsSeatsItem>>> => ({
    ...checkoutSeatSelections,
    ...flightSeatSelections,
  }),
)

const createTravellerElements = (travellers: Array<{ type: string }> = [], penaltyBreakdown: Record<string, App.FlightChangePenaltyBreakdown> = {}) => {
  const intl = getCurrentIntl()
  const travellerCounts = {
    adult: travellers.filter(t => t.type === 'adult').length,
    child: travellers.filter(t => t.type === 'child').length,
    infant: travellers.filter(t => t.type === 'infant').length,
  }

  const priceMap = {
    adult: penaltyBreakdown.adult?.amount || 0,
    child: penaltyBreakdown.child?.amount || 0,
    infant: penaltyBreakdown.infant?.amount || 0,
  }

  // Only return elements for traveller types that have passengers
  return Object.entries(travellerCounts)
    .filter(([_, count]) => count > 0) // This ensures items are only visible when we have that type of passenger
    .map(([type, count]) => ({
      id: type,
      left: {
        type: 'text' as const,
        value: formatTravellerCount(intl, type, count),
      },
      right: {
        type: 'price' as const,
        value: priceMap[type as keyof typeof priceMap] * count,
      },
    }))
}

export const getFlightItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.FlightItem> => items.filter(isFlightItem),
  reselectShallowEqual,
)

export const getCharterFlightItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.FlightItem> => items
    .filter(item => isFlightItem(item))
    .filter(item => item.charterBundleRoute),
  reselectShallowEqual,
)

export const getNonCharterFlightItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.FlightItem> => items
    .filter(item => isFlightItem(item))
    .filter(item => !item.charterBundleRoute),
  reselectShallowEqual,
)

export const getIsCharterFlightCheckout = (state: App.State) => getCharterFlightItems(state).length > 0

export const getCharterFlightDepartureDate = createSelector(
  (state: App.State) => getCharterFlightItems(state),
  (charterFlightItems): string | undefined => charterFlightItems[0]?.flights.at(0)?.departingDate,
)

export const getCharterFlightReturnArrivalDate = createSelector(
  (state: App.State) => getCharterFlightItems(state),
  (charterFlightItems): string | undefined => {
    const arrivalDateTime = charterFlightItems[0]?.flights.at(-1)?.arrivalDateTime
    return arrivalDateTime?.split('T')[0]
  },
)

export const flightSearchInProgress = createSelector(
  (state: App.State) => state.flights.searchFlights,
  (state: App.State) => state.flights.searchV2Flights,
  (flightsV1, flightsV2): boolean => {
    const journeysV1Fetching = Object.values(flightsV1).flatMap(flight => flight?.fetching).filter(Boolean)
    const journeysV2Fetching = Object.values(flightsV2).flatMap(flight => flight?.fetching).filter(Boolean)
    return !!(journeysV1Fetching.length || journeysV2Fetching.length)
  },
)

export const getBundledFlightItems = createSelector(
  (state: App.State) => getFlightItems(state),
  (items): Array<App.Checkout.FlightItem> => items.filter(item => !!item.bundledItemIds?.length),
  reselectShallowEqual,

)

export const cartIncludesFlights = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): boolean => items.some(isFlightItem),
)

function mapFlightV2ToView(
  item: App.Checkout.FlightItem,
  airports: Array<App.Airport>,
  journeys: Record<string, App.JourneyV2> = {},
): App.Checkout.FlightItemView {
  const intl = getCurrentIntl()
  const fares = sum(item.flights.map(flight =>
    flight.fareFamily?.cost?.all ?? flight.cost ?? 0,
  ))
  const surcharge = sum(item.flights.map(flight => journeys[flight.journeyId]?.price.all.totalFees ?? 0))

  const isBundled = (item.bundledItemIds?.length ?? 0) > 0

  const journeysFareTypes = item.flights.map(flight => journeys[flight.journeyId]?.fareType)

  const totalFare = item.totalFare ?? 0
  const isReturnFares = journeysFareTypes.includes(FlightViewTypes.RETURN)
  let price = totalFare > 0 ? totalFare : fares

  const departing = item.flights[0] ? journeys[item.flights[0].journeyId] : undefined
  const returning = item.flights[1] ? journeys[item.flights[1].journeyId] : undefined

  const isNDC = Object.values(journeys).some(journey => journey?.bookingInfo?.offerItemId && journey?.bookingInfo?.offerId)

  if (isReturnFares) {
    // For return fares, if departing and returning is defined, we must use the sum of departingFare and returning fare
    // This happens because after updating the fare with an upsell fare family, each segment total fare is updated individually
    // If the only departing is defined, we can use the totalRoundTripPrice since it represents the price considering for
    // the selected departing flight and the cheapest returning flight paired within it
    price = returning ? fares : (departing?.price.all.totalRoundTripPrice ?? 0)
  } else if (departing && !returning && item.viewType !== FlightViewTypes.ONE_WAY) {
    // For V2 two one-ways, if the returning fare is not defined, we must use the departing.price.all.totalFare because
    // it has the updated departing flight price after selecting the fare family. The item.departing.cost is only updated
    // after the select endpoint call. We must also add the item.quotedFare price because the cheapest flight price
    // is already included in the total Hotel + flights package. In order to have correct increase in price based on the relative price
    // displayed in the flight tile, we must add the quotedFare to the total price
    price = departing.price.all.totalFare + (item.quotedFare ?? 0) / 2
  }

  const flightsWithJourneys = item.flights.filter(flight => journeys[flight.journeyId])

  const airportsByKey = arrayToMap(airports, airport => airport.code)

  const usedAirports: Array<{ departing?: App.Airport, arrival?: App.Airport }> = flightsWithJourneys.map(journey => {
    const departing = airportsByKey.get(journey.departingAirportCode)
    const arrival = airportsByKey.get(journey.arrivalAirportCode)

    return {
      departing,
      arrival,
    }
  })

  const firstDepartingCountryCode = usedAirports[0]?.departing?.countryCode

  const isDomestic = usedAirports.every(airport => {
    return airport.arrival?.countryCode === airport.departing?.countryCode && airport.departing?.countryCode === firstDepartingCountryCode
  })

  const loyaltyProductTypes = flightsWithJourneys.map(journey => {
    return getFlightLoyaltyProductType(journey.fareFamily?.cabin?.toLowerCase() || item.selectedCabinClass || '', isDomestic)
  })

  const rankedProductTypes = loyaltyProductTypes.sort((a, b) => {
    return getFlightLoyaltyProductTypeRank(a) - getFlightLoyaltyProductTypeRank(b)
  })

  const flights = flightsWithJourneys.map(flight => ({
    journeyFlight: convertJourneyV2ToJourneyFlight(journeys[flight.journeyId]),
    extras: flight.extras,
    cost: flight.cost,
    fareFamily: flight.fareFamily,
    withEmbeddedInsurance: flight.withEmbeddedInsurance,
  }))

  const result: App.Checkout.FlightItemView = {
    title: `${flights.map(flight => formatJourneyShorthand(flight.journeyFlight, intl)).join(' - ')} - ${formatFareType(intl, item.fareType)}`,
    loyalty: {
      productType: rankedProductTypes[0] || 'dom_economy',
    },
    item,
    journeyId: item.searchId,
    provider: departing?.provider,
    passengers: item.passengers,
    flights,
    designation: intl.formatMessage({
      id: 'PWHuXf7EtR',
      defaultMessage: 'Flights',
      description: 'Flight item designation in checkout breakdown in the checkout flow',
    }),
    validatingCarrier: departing?.carrier,
    validatingCarrierName: departing?.carrierName ?? '',
    validatingCarrierLogo: departing?.carrierLogo ?? '',
    returningCarrierName: returning?.carrierName ?? '',
    returningCarrierLogo: returning?.carrierLogo ?? '',
    fareType: item.fareType,
    totals: {
      price,
      memberPrice: 0, // No data on this
      value: price,
      surcharge,
      memberValue: 0,
      extraGuestSurcharge: 0,
      taxesAndFees: 0,
      otherFees: item.otherFees,
    },
    seatSelectionPolicy: departing?.flightGroup.flights[0].seatSummary.selectionPolicy,
    isBundled,
    quotedFare: item.quotedFare,
    searchFlightsUrl: isBundled ?
        getCheckoutFlightV2SearchUrl(flightsWithJourneys.map(flight => journeys[flight.journeyId])) :
        getStandaloneFlightsV2SearchUrl(item, airports, journeys),
    viewType: item.viewType,
    isNDC,
    charterBundleRoute: item.charterBundleRoute,
  }

  return result
}

export const getFlightItemsView = createSelector(
  (state: App.State) => getFlightItems(state),
  (state: App.State) => state.flights.journeysById,
  (state: App.State) => state.geo.airports,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (flightItems, journeys, airports, postPurchaseMode): App.WithDataStatus<Array<App.Checkout.FlightItemView>> => {
    const anyItemsFetching = flightItems.some(item => item.fetchingFare)

    const flightViews = nonNullable(
      flightItems.map<{ hasRequiredData: boolean, view: App.Checkout.FlightItemView | undefined }>((item) => {
        let journeyKeys: Array<string>
        if (postPurchaseMode === 'flight-change-dates') {
          journeyKeys = item.flights.map(flight => getDateChangeJourneyV2IdKey(item.reservationId!, item.searchId!, flight?.journeyId))
        } else {
          journeyKeys = item.flights.map(flight => getJourneyV2IdKey(flight?.journeyId, item.searchId!, flight?.fareFamily?.id))
        }
        const flightJourneys = journeyKeys.map(key => journeys[key] as App.JourneyV2)
        const journeysByFlightJourneyId = arrayToObject(item.flights, flight => flight.journeyId, (_, index) => flightJourneys[index])

        return {
          view: mapFlightV2ToView(
            item,
            airports,
            journeysByFlightJourneyId,
          ),
          hasRequiredData: flightJourneys.filter(Boolean).length === item.flights.length,
        }
      }),
    )

    const hasAllRequiredData = flightViews.every(fv => fv.hasRequiredData) && !anyItemsFetching && flightViews.length === flightItems.length

    return {
      hasRequiredData: hasAllRequiredData,
      data: nonNullable(flightViews.map(view => view.view)),
    }
  },
)

export const getFlightBreakdownView = createSelector(
  (state: App.State) => getFlightItemsView(state),
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => state.checkout.commissionData,
  (flightViewsWithStatus, postPurchaseMode, commissionData): App.WithDataStatus<Array<App.Checkout.PriceBreakdownView>> => {
    const intl = getCurrentIntl()
    const flightBreakdownViews = flightViewsWithStatus.data.map<App.Checkout.PriceBreakdownView>(view => {
      let title = formatFareType(intl, view.fareType)
      if (postPurchaseMode === 'flight-change-dates') {
        title = intl.formatMessage({
          id: 'VzFTPDIUqb',
          defaultMessage: 'New flight additional cost',
          description: 'Title for flight date change additional cost in checkout',
        })
      }
      const supplementaryData = {
        validatingCarrierName: view.validatingCarrierName,
        validatingCarrierLogo: view.validatingCarrierLogo,
      }
      const flightItems = view.flights.map(flight => buildFlightItemBreakdownView(view, flight, supplementaryData, intl))

      const breakdownView: App.Checkout.PriceBreakdownView = {
        title,
        breakdownItemType: 'flight',
        additionalInfoText: postPurchaseMode === 'flight-change-dates' ? [] : [
          ...(view.flights[0] ? [formatJourneySummaryTitle(view.flights, view.fareType as FlightsFareTypes, intl)] : []),
          formatFlightPassengersShorthand(view.passengers, intl),
        ],
        price: view.isBundled ? 0 : (view.totals.price ?? 0),
        memberPrice: view.isBundled ? 0 : (view.totals.memberPrice ?? 0),
        items: postPurchaseMode === 'flight-change-dates'
          ? [buildDateChangeFlightItemBreakdownView(view, intl)]
          : flightItems,
        commission: commissionData?.itemsCommission?.[getAgentHubCommissionItemKey(view.item)],
      }

      return breakdownView
    })

    return {
      hasRequiredData: flightViewsWithStatus.hasRequiredData,
      data: flightBreakdownViews,
    }
  },
)

export const getAirlineChangeFeeBreakdownView = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder,
  (state: App.State) => state.orders.flightDetails,
  (order, allFlightDetails): App.Checkout.PriceBreakdownView => {
    const intl = getCurrentIntl()
    const flightItemId = order?.flightItems[0]?.id
    const flightDetails = allFlightDetails[flightItemId ?? '']
    const travellers = flightDetails?.flight?.travellers || []
    const price = flightDetails?.flight?.flightChangePenalties?.fees.totalAmount || 0
    const vendorFeeBreakdown = flightDetails?.flight?.flightChangePenalties?.fees.vendorFeeByCarrier[0]?.penaltyBreakdown || {}
    const gstAmount = flightDetails?.flight?.flightChangePenalties?.fees.gstAmount ?? 0

    const items: Array<App.Checkout.AnyItemBreakdownView> = [
      {
        itemType: 'flights-date-change-fee',
        title: '',
        additionalInfo: [],
        additionalElements: createTravellerElements(travellers, vendorFeeBreakdown),
      },
    ]

    if (gstAmount > 0) {
      items.unshift({
        itemType: 'flights-date-change-fee',
        title: intl.formatMessage({
          id: 'AdbpBT+M5t',
          defaultMessage: 'GST',
          description: 'Title for GST applicable to airline change fee breakdown',
        }),
        price: gstAmount,
        additionalInfo: [],
        additionalElements: [],
      })
    }

    return {
      title: intl.formatMessage({
        id: 'bX8zkq3FJx',
        defaultMessage: 'Airline change fee',
        description: 'Title for airline change fee in flight date change breakdown',
      }),
      price,
      alwaysShowItemPrice: true,
      additionalInfoText: [],
      items,
      memberPrice: 0,
    }
  },
)

export const getLuxuryEscapesChangeFeeBreakdownView = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder,
  (state: App.State) => state.orders.flightDetails,
  (order, allFlightDetails): App.Checkout.PriceBreakdownView => {
    const intl = getCurrentIntl()
    const flightItemId = order?.flightItems[0]?.id
    const flightDetails = allFlightDetails[flightItemId ?? '']
    const travellers = flightDetails?.flight?.travellers || []
    const luxuryEscapesChangeFee = flightDetails?.flight?.flightChangePenalties?.fees.handlingFee || 0
    const handlingFeeBreakdown = flightDetails?.flight?.flightChangePenalties?.fees.handlingFeeBreakdown || {}

    return {
      title: intl.formatMessage(
        {
          id: 'aKwBa4vmO3',
          defaultMessage: '{brandName} change fee',
          description: 'Title for Luxury Escapes change fee in flight date change breakdown',
        },
        { brandName: 'Luxury Escapes' },
      ),
      price: luxuryEscapesChangeFee,
      alwaysShowItemPrice: true,
      additionalInfoText: [],
      items: [
        {
          itemType: 'flights-date-change-fee',
          title: '',
          additionalInfo: [],
          additionalElements: createTravellerElements(travellers, handlingFeeBreakdown),
        },
      ],
      memberPrice: 0,
    }
  },
)

export const getFlightsDateChangeBreakdownView = createSelector(
  (state: App.State) => getFlightItemsView(state),
  (state: App.State) => getAirlineChangeFeeBreakdownView(state),
  (state: App.State) => getLuxuryEscapesChangeFeeBreakdownView(state),
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (flightViewsWithStatus, airlineChangeFeeBreakdownView, luxuryEscapesChangeFeeBreakdownView, postPurchaseMode): App.WithDataStatus<Array<App.Checkout.PriceBreakdownView>> => {
    if (postPurchaseMode !== 'flight-change-dates') {
      return {
        hasRequiredData: flightViewsWithStatus.hasRequiredData,
        data: [],
      }
    }

    return {
      hasRequiredData: flightViewsWithStatus.hasRequiredData,
      data: [airlineChangeFeeBreakdownView, luxuryEscapesChangeFeeBreakdownView],
    }
  },
)

export const isStandaloneFlights = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => getFlightItems(state),
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => state.checkout.cart.existingOrder,
  (allItems, flightItems, postPurchaseMode, order): boolean => {
    if (postPurchaseMode) {
      if (!order) return false
      return !!getAllOrderItems(order)?.every(
        item => isFlightOrderItem(item) || isInsuranceOrderItem(item) || isBookingProtectionOrderItem(item) || isLuxPlusSubscriptionOrderItem(item),
      )
    }

    // a standalone flight can still have insurance/booking protection and/or a luxplus subscription
    return flightItems.length > 0 && allItems.every(item => isFlightItem(item) || isInsuranceItem(item) || isBookingProtectionItem(item) || isLuxPlusSubscriptionItem(item))
  },
)

export const checkoutContainsFlightsWithEmbeddedInsurance = createSelector(
  (state: App.State) => getFlightItems(state),
  (flightItems) => hasCheckoutFlightItemsWithEmbeddedInsurance(flightItems),
)

/**
 * Returns aggregated flight addon rows (seat selections, baggage, meals, etc.)
 * computed directly from flight breakdown views and seat selections.
 */
export const getFlightAddonRows = createSelector(
  (state: App.State) => getFlightBreakdownView(state),
  (state: App.State) => getFlightSeatSelectionsByKey(state),
  (flightBreakdownViewWithStatus, seatSelectionsByKey): Array<FlightServiceRowData> => {
    const flightItems = flightBreakdownViewWithStatus.data
      .flatMap(breakdown => breakdown.items)
      .filter((item): item is App.Checkout.FlightItemBreakdownView => item.itemType === 'flight')

    return aggregateFlightServiceRows(flightItems, seatSelectionsByKey)
  },
)

export const getFlightNumbersBySearchId = createSelector(
  (state: App.State) => getFlightItems(state),
  (state: App.State) => state.flights.seatsMap,
  (flightItems, seatMaps) => {
    const flightNumberMap: Record<string, Array<string>> = {}

    Object.values(seatMaps).forEach((values) => {
      if (values?.data?.seatMap && !values?.fetching) {
        values.data.seatMap.forEach((seatMap) => {
          const flightItem = flightItems.find((item) => {
            return item.flights.find((flight) => flight.journeyId === seatMap.fareId)
          })

          const searchId = flightItem?.searchId ?? ''

          const mappedSearchId = flightNumberMap[searchId] ?? []
          mappedSearchId.push(seatMap.paxSegmentRefID)
          flightNumberMap[searchId] = mappedSearchId
        })
      }
    })

    return flightNumberMap
  },
)
